import { comTokenInscricao } from "@/lib/inscricao-acesso";
import { Option } from "@/components/ui/multiple-selector";
import { arquivosModel } from "@/interfaces/zod";
import { type ClassValue, clsx } from "clsx";
import groupby from "lodash.groupby";
import type { BaseSyntheticEvent } from "react";
import { formatDate, getAge as getAgeFromDate, parseDate } from "./date-utils";
import { FieldErrors } from "react-hook-form";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import i18n from "./i18n";
import { getApiBaseUrl } from "./rtk-query-config";

/** Exibe `toast.promise` e devolve a mesma promise para `await` (Sonner 1.x não expõe `.unwrap()` no toast). */
export function toastPromiseUnwrap<T>(
  promise: Promise<T>,
  toastData: NonNullable<Parameters<typeof toast.promise>[1]>,
): Promise<T> {
  void toast.promise(promise, toastData);
  return promise;
}

export function getPublicUrl(arquivo: z.infer<typeof arquivosModel>) {
  return arquivo
    ? `https://minio.sysigreja.com/sysigreja-public-${arquivo.LoginId}/${arquivo.Id}.${arquivo.Extensao}`
    : "";
}

export function clearPhone(phone?: string) {
  return phone?.replace(/[().\s-]/g, "");
}

/**
 * Formata telefone para exibição (remove +55 e formata no padrão brasileiro)
 */
export function formatPhoneForDisplay(phone?: string): string {
  if (!phone) return "";

  // Remove todos os caracteres não numéricos
  const numbers = phone.replace(/\D/g, "");

  // Remove código do país se existir (+55)
  const numbersWithoutCountry =
    numbers.startsWith("55") && numbers.length > 10
      ? numbers.slice(2)
      : numbers;

  // Aplica formatação brasileira
  if (numbersWithoutCountry.length === 11) {
    // (11) 99999-9999
    return `(${numbersWithoutCountry.slice(
      0,
      2
    )}) ${numbersWithoutCountry.slice(2, 7)}-${numbersWithoutCountry.slice(7)}`;
  } else if (numbersWithoutCountry.length === 10) {
    // (11) 9999-9999
    return `(${numbersWithoutCountry.slice(
      0,
      2
    )}) ${numbersWithoutCountry.slice(2, 6)}-${numbersWithoutCountry.slice(6)}`;
  }

  // Se não conseguir formatar, retorna o original sem +55
  return phone.replace(/^\+55\s*/, "");
}

/**
 * Prepara telefone para link do WhatsApp (remove +55 e mantém apenas números com código do país)
 */
export function formatPhoneForWhatsApp(phone?: string): string {
  if (!phone) return "";

  // Remove todos os caracteres não numéricos
  let numbers = phone.replace(/\D/g, "");

  // Remove código do país se existir para normalizar
  if (numbers.startsWith("55") && numbers.length > 10) {
    numbers = numbers.slice(2);
  }

  // Adiciona código do país 55 (Brasil) se tiver 10 ou 11 dígitos
  if (numbers.length >= 10 && numbers.length <= 11) {
    numbers = "55" + numbers;
  }

  return numbers;
}

export async function downloadFileAsBase64(
  url: string
): Promise<string | undefined> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }
    const blob = await response.blob();
    const base64String = await blobToBase64(blob);
    return base64String;
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
}

/** Achata erros do react-hook-form (incl. arrays e objetos aninhados) para mensagens legíveis. */
function flattenRhfFieldErrors(errors: FieldErrors | null | undefined): { path: string; message: string }[] {
  const results: { path: string; message: string }[] = [];

  function pushMessage(path: string, msg: unknown) {
    if (typeof msg === "string" && msg.trim()) {
      results.push({ path: path || "(formulário)", message: msg.trim() });
    }
  }

  function visit(node: unknown, path: string): void {
    if (node == null || typeof node !== "object") return;
    const n = node as Record<string, unknown>;

    pushMessage(path, n.message);

    if (n.types && typeof n.types === "object") {
      for (const v of Object.values(n.types)) {
        pushMessage(path, v);
      }
    }

    for (const key of Object.keys(n)) {
      if (key === "message" || key === "type" || key === "ref" || key === "types") continue;
      const child = n[key];
      const childPath = path ? `${path}.${key}` : key;

      if (Array.isArray(child)) {
        child.forEach((item, i) => {
          visit(item, `${childPath}.${i}`);
        });
      } else if (child && typeof child === "object") {
        visit(child, childPath);
      }
    }
  }

  if (!errors) return results;
  for (const key of Object.keys(errors)) {
    visit(errors[key as keyof typeof errors], key);
  }
  return results;
}

function safeJsonSnippet(value: unknown, maxLen: number): string {
  try {
    const s = JSON.stringify(value);
    if (s.length <= maxLen) return s;
    return `${s.slice(0, maxLen)}…`;
  } catch {
    return "[não serializável]";
  }
}

export type HandleFormErrorsOptions = {
  /** Envia erros achatados para a API de logs (postLog), quando houver falha de validação. */
  postLogTipo?: string;
  postLogExtra?: Record<string, unknown>;
};

export function isReactSyntheticEvent(x: unknown): x is BaseSyntheticEvent {
  return typeof x === "object" && x !== null && "nativeEvent" in x;
}

/** Rótulos pt-BR para nomes internos de campos (DataNascimento, Fone...) nos toasts de validação. */
const ROTULOS_CAMPOS_FORMULARIO: Record<string, string> = {
  datanascimento: "Data de nascimento",
  fone: "Telefone",
  telefone: "Telefone",
  celular: "Celular",
  apelido: "Apelido",
  nome: "Nome",
  email: "E-mail",
  sexo: "Gênero",
  cpf: "CPF",
  cep: "CEP",
  endereco: "Endereço",
  numero: "Número",
  bairro: "Bairro",
  cidade: "Cidade",
  estado: "Estado",
  complemento: "Complemento",
  igrejaid: "Igreja",
  igreja: "Igreja",
  estadocivil: "Estado civil",
  nomeconjuge: "Nome do cônjuge",
  foneconjuge: "Telefone do cônjuge",
  nomecontato: "Nome do contato",
  fonecontato: "Telefone do contato",
};

/** Converte o path do RHF/Zod em rótulo legível; sem rótulo conhecido, retorna null (só a mensagem). */
export function rotuloCampoFormulario(path: string): string | null {
  const ultimo = path.split(".").filter((p) => p && !/^\d+$/.test(p)).pop() ?? "";
  return ROTULOS_CAMPOS_FORMULARIO[ultimo.toLowerCase()] ?? null;
}
/** Compatível com `handleSubmit(_, onInvalid)` do RHF (2º arg = evento) e com chamadas diretas `(errors, options?)`. */
export const handleFormErrors = (
  e: FieldErrors,
  second?: BaseSyntheticEvent | HandleFormErrorsOptions,
) => {
  const options: HandleFormErrorsOptions | undefined =
    second !== undefined && !isReactSyntheticEvent(second) ? second : undefined;
  const flagValue = (window as any).__validandoSecaoParcial;

  // Evitar exibir toasts durante validação parcial de seções
  // Se houver uma flag global indicando validação parcial, não exibir toasts
  if (flagValue) {
    return;
  }

  const flat = flattenRhfFieldErrors(e);

  if (options?.postLogTipo) {
    const payload = {
      Tipo: options.postLogTipo,
      Objeto: {
        ...options.postLogExtra,
        erros: flat,
        ...(flat.length === 0 ? { erroDump: safeJsonSnippet(e, 6000) } : {}),
      },
    };
    // Import dinâmico evita ciclo estático: utils → logs → store → slices → utils
    void import("@/lib/logs").then(({ postLog }) => postLog(payload));
  }

  console.warn("[handleFormErrors] Validação Zod/RHF", { flat, errors: e });

  if (flat.length === 0) {
    toast.error("Não foi possível validar o formulário. Verifique os campos obrigatórios e formatos.");
    return;
  }

  const seen = new Set<string>();
  for (const { path, message } of flat) {
    const label = path && path !== "(formulário)" ? rotuloCampoFormulario(path) : null;
    const line = label ? `${label}: ${message}` : message;
    if (seen.has(line)) continue;
    seen.add(line);
    toast.error(line);
  }
};

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result.split(",")[1]); // Extract Base64 part
      } else {
        reject(new Error("FileReader result is not a string"));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
export function PaperSize(paper: string, orientacao: string = "Retrato") {
  switch (paper) {
    case "Carta":
      return `216mm 279mm ${
        orientacao === "Retrato" ? "portrait" : "landscape"
      }`;
    case "A3":
      return `297mm 420mm ${
        orientacao === "Retrato" ? "portrait" : "landscape"
      }`;
    default:
      return `210mm 297mm ${
        orientacao === "Retrato" ? "portrait" : "landscape"
      }`;
  }
}
export function PaperWidth(paper: string) {
  switch (paper) {
    case "Carta":
      return 216;
    case "A3":
      return 297;
    default:
      return 210;
  }
}
export function PaperHeight(paper: string) {
  switch (paper) {
    case "Carta":
      return 279;
    case "A3":
      return 420;
    default:
      return 297;
  }
}

export async function getBase64Image(res) {
  const blob = res.data;
  const reader = new FileReader();

  await new Promise((resolve, reject) => {
    reader.onload = resolve;
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  return reader.result.toString();
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function filterOptions(search: string, options: Option[]) {
  return options.filter((x) =>
    normalizeString(x.label || "").includes(normalizeString(search || ""))
  );
}

export function normalizeString(str: string) {
  return (
    str
      ?.normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase() || ""
  );
}

export const nullableString = () =>
  z
    .string()
    .nullish()
    .transform((v) => v || "")
    .pipe(z.string());

export function slug(text: string | undefined | null) {
  if (!text) return "";
  return text
    .toString()
    ?.toLowerCase()
    .normalize("NFD") // Decompor caracteres acentuados em base + acento
    .replace(/[\u0300-\u036f]/g, "") // Remover os acentos (diacríticos)
    .replace(/ç/g, "c") // Substituir 'ç' por 'c'
    .replace(/\s+/g, "-") // Substituir espaços por '-'
    .replace(/[^\w-]+/g, "") // Remover caracteres não alfanuméricos, exceto '-'
    .replace(/--+/g, "-"); // Substituir múltiplos '-' por um único '-'
}

/** Identificador público na URL do evento: só `a-z`, `0-9` e `-` (sem espaços, `_` nem outros símbolos). */
export function slugIdentificadorUrl(text: string | undefined | null): string {
  const s = slug(text).replace(/_/g, "");
  return s.replace(/[^a-z0-9-]/g, "").replace(/--+/g, "-").replace(/^-+|-+$/g, "");
}
export function initials(name: string) {
  const rgx = new RegExp(/(\p{L}{1})\p{L}+/gu);

  if (name) {
    const initials = [...name.matchAll(rgx)];

    return (
      (initials.shift()?.[1] || "") + (initials.pop()?.[1] || "")
    ).toUpperCase();
  }

  return "";
}


/** Primeiro token não vazio após trim (ex.: nome completo → primeiro nome). */
export function primeiroNomeDoNomeCompleto(nome: string | null | undefined): string {
  if (!nome?.trim()) return "";
  const part = nome.trim().split(/\s+/).find(Boolean);
  return part ?? "";
}
export function generateUID() {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16)
  );
}

export function currency(value: number) {
  return (typeof value == "number" ? value : parseFloat(value)).toLocaleString(
    i18n.language,
    { style: "currency", currency: i18n.language === "en" ? "USD" : "BRL" }
  );
}

/** Inteiro para exibição com separadores de milhar conforme idioma (ex.: 1.234 em pt-BR). */
export function formatInteger(value: number): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.trunc(n).toLocaleString(i18n.language, {
    maximumFractionDigits: 0,
    useGrouping: true,
  });
}

export function getAge(date: Date) {
  return getAgeFromDate(date);
}

/**
 * Monta o nome do arquivo ao baixar: evita `nome.xlsx` + ext `XLSX` virar `nome.xlsxXLSX`
 * e garante extensão quando só há `Nome` base ou só `Extensao`.
 */
export function arquivoNomeCompletoParaDownload(params: {
  /** Campo `Nome` do backend (pode ser só base ou nome já com extensão). */
  nome?: string | null;
  /** Campo `Extensao` do backend (sem ponto). */
  extensao?: string | null;
  /** Fallback: string única (ex.: `Nome` já completo vindo da listagem). */
  filename?: string | null;
}): string {
  const extRaw = (params.extensao ?? "").trim().replace(/^\./, "");
  const nomeRaw = (params.nome ?? "").trim();
  const legacy = (params.filename ?? "").trim();

  const primary = nomeRaw || legacy;
  if (!primary && extRaw) {
    return `arquivo.${extRaw}`;
  }
  if (!primary) {
    return "arquivo.bin";
  }
  if (!extRaw) {
    return primary;
  }
  const el = extRaw.toLowerCase();
  if (primary.toLowerCase().endsWith(`.${el}`)) {
    return primary;
  }
  return `${primary}.${extRaw}`;
}

export function downloadFile(result: any, filename: string) {
  if (!result?.data) {
    console.error("Download: dados inválidos");
    return { data: null };
  }
  const hiddenElement = document.createElement("a");
  const url = window.URL || window.webkitURL;
  const blob = url.createObjectURL(result.data);
  hiddenElement.href = blob;
  hiddenElement.target = "_blank";
  hiddenElement.download = filename;
  document.body.appendChild(hiddenElement);
  hiddenElement.click();
  document.body.removeChild(hiddenElement);
  setTimeout(() => url.revokeObjectURL(blob), 100);
  return { data: null };
}

export function randomColor() {
  return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
}

export const brDateFormats = [
  "DDMMYY",
  "DDMMYYYY",
  "DD/MM/YY",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD.MM.YYYY",
  "D.M.YYYY",
  "DD. MM. YYYY",
  "D. M. YYYY",
];

function resolveGroupRowKey(row, groupRow) {
  if (groupRow.getGroupKey) {
    const data = row?.original ?? row;
    const key = groupRow.getGroupKey(data);
    return key == null || key === "" ? "" : String(key);
  }

  if (!groupRow.prop) return "";

  if (groupRow.prop.includes("|")) {
    const props = groupRow.prop.split("|");
    const firstProp = row?.original?.[props[0]] || row?.[props[0]];
    if (Array.isArray(firstProp)) {
      return firstProp.length > 0 ? String(firstProp[0][props[1]] ?? "") : "";
    }
    if (props.length >= 4) {
      return String(
        firstProp?.[props[1]]?.[props[2]]?.[props[3]] ?? "",
      );
    }
    if (props.length === 3) {
      return String(firstProp?.[props[1]]?.[props[2]] ?? "");
    }
    return firstProp ? String(firstProp[props[1]] ?? "") : "";
  }

  if (groupRow.prop === "DataCadastro" && groupRow.formatDate) {
    const date = row?.original?.[groupRow.prop] || row?.[groupRow.prop];
    if (date) {
      try {
        return formatDate(date, "DD/MM/YYYY");
      } catch (error) {
        console.warn("Erro ao formatar data:", error);
        return String(date);
      }
    }
    return "";
  }

  const value = row?.original?.[groupRow.prop] || row?.[groupRow.prop];
  return value == null || value === "" ? "" : String(value);
}

export const groupRows = (rows, groupRow) => {
  if (!groupRow || (!groupRow.prop && !groupRow.getGroupKey)) {
    return {};
  }

  const sourceRows = groupRow.preserveInputOrder
    ? rows
    : [...rows].sort((rowA, rowB) => {
        const keyA = resolveGroupRowKey(rowA, groupRow);
        const keyB = resolveGroupRowKey(rowB, groupRow);
        return keyA.localeCompare(keyB, "pt-BR", { sensitivity: "base" });
      });

  return groupby(sourceRows, (row) => resolveGroupRowKey(row, groupRow));
};

export const isImage = (ext) => {
  const imageExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "webp",
    "tiff",
    "svg",
  ];
  return imageExtensions.some((x) =>
    x.toLowerCase().endsWith(ext?.toLowerCase())
  );
};
export const isPDF = (ext) => {
  const imageExtensions = ["pdf"];
  return imageExtensions.some((x) =>
    x.toLowerCase().endsWith(ext?.toLowerCase())
  );
};

const emailSchema = z.string().email();

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));
  return `${formattedSize} ${sizes[i]}`;
};
// Função para validar o email avulso
export const validateEmail = (email: string) => {
  try {
    emailSchema.parse(email);
    return true;
  } catch (e) {
    return false;
  }
};

export const cleanFone = (fone?: string) =>
  fone
    ?.toString()
    .replaceAll(" ", "")
    .replaceAll("+", "")
    .replaceAll("(", "")
    .replaceAll(")", "")
    .replaceAll(".", "")
    .replaceAll("-", "");

export const wppFone = (fone: string) => `${cleanFone(fone)}`;

export const arrayUniqueByKey = (array, key) =>
  array?.some((x) => x)
    ? [
        ...new Map(
          array.map((item) => {
            return [item[key], item];
          })
        ).values(),
      ]
    : [];

export function getLogoUrl(login: {
  Id: string | number;
  logo?: { Id: string | number; Extensao: string } | null;
}) {
  if (!login?.logo?.Id || !login?.logo?.Extensao) {
    return null;
  }
  return `https://minio.sysigreja.com/sysigreja-public-${login.Id}/${login.logo.Id}.${login.logo.Extensao}`;
}

/**
 * Normaliza dados de tabela criando novas referências e adiciona hash pré-calculado
 * para otimizar comparações em componentes memoizados.
 * 
 * @param data - Array de dados da tabela (participantes ou equipantes)
 * @returns Array normalizado com _etiquetasHash para comparação O(1)
 */
export function normalizeTableDataWithHash(data: any[]): any[] {
  if (!data || data.length === 0) return data;
  
  return data.map((row: any) => {
    // Voluntários: equipantes.participantesetiquetas (2 níveis)
    if (row.equipantes?.participantesetiquetas) {
      const etiquetas = row.equipantes.participantesetiquetas;
      
      // Criar nova referência do array
      const newEtiquetas = [...etiquetas];
      
      // Calcular hash: IDs ordenados e unidos por vírgula
      // Ordenação garante que mesmo conjunto de IDs sempre gere mesmo hash
      const etiquetasIds = newEtiquetas
        .map((e: any) => e.etiquetas?.Id)
        .filter(Boolean)
        .sort((a, b) => Number(a) - Number(b));
      
      const etiquetasHash = etiquetasIds.length > 0 
        ? etiquetasIds.join(',') 
        : '';
      
      // [DEBUG] Log para verificar se hash está sendo calculado
      if (import.meta.env.DEV && row.Id) {
        console.log(`[NORMALIZE_HASH] Row ${row.Id}: hash=${etiquetasHash}, etiquetasCount=${newEtiquetas.length}`);
      }
      
      return {
        ...row,
        equipantes: {
          ...row.equipantes,
          participantesetiquetas: newEtiquetas,
          // Hash pré-calculado para comparação O(1) em areEqual
          _etiquetasHash: etiquetasHash,
        }
      };
    }
    
    // Participantes: participantesetiquetas (1 nível)
    if (row.participantesetiquetas) {
      const etiquetas = row.participantesetiquetas;
      const newEtiquetas = [...etiquetas];
      
      const etiquetasIds = newEtiquetas
        .map((e: any) => e.etiquetas?.Id)
        .filter(Boolean)
        .sort((a, b) => Number(a) - Number(b));
      
      const etiquetasHash = etiquetasIds.length > 0 
        ? etiquetasIds.join(',') 
        : '';
      
      return {
        ...row,
        participantesetiquetas: newEtiquetas,
        // Hash pré-calculado para comparação O(1)
        _etiquetasHash: etiquetasHash,
      };
    }
    
    return row;
  });
}

/**
 * Achata estrutura de dados de voluntários movendo propriedades de equipantes.* para a raiz.
 * Isso iguala a estrutura à de participantes, facilitando detecção de mudanças pelo React.
 * 
 * IMPORTANTE: Recria objetos aninhados (eventos, presencareuniaos) com novas referências
 * para garantir que o React detecte mudanças quando apenas dados aninhados são atualizados.
 * 
 * @param data - Array de dados de voluntários com estrutura aninhada
 * @returns Array com estrutura achatada (equipantes.* movido para raiz)
 */
export function flattenEquipantesData(data: any[]): any[] {
  if (!data || data.length === 0) {
    return data;
  }
  
  const result = data.map((row: any) => {
    if (!row.equipantes) {
      return row;
    }
    
    const { equipantes, eventos, presencareuniaos, ...rest } = row;
    
    // Preservar o ID do EquipanteEvento antes do spread de equipantes
    // (que sobrescreveria o Id da raiz com o Id do equipante)
    const equipanteEventoId = row.Id;
    
    // Recriar objetos aninhados com novas referências para garantir detecção de mudanças pelo React
    const newEventos = eventos ? {
      ...eventos,
      // Recriar reuniaoeventos se existir
      reuniaoeventos: eventos.reuniaoeventos ? [...eventos.reuniaoeventos] : eventos.reuniaoeventos,
    } : eventos;
    
    const newPresencareuniaos = presencareuniaos ? [...presencareuniaos] : presencareuniaos;
    
    const eventObservacao =
      rest.Observacao ?? rest.equipanteeventos?.Observacao;
    const eventNota = rest.Nota ?? rest.equipanteeventos?.Nota;

    const flattened = {
      ...equipantes, // Todas as propriedades de equipantes na raiz (incluindo _etiquetasHash)
      equipantesId: equipantes.Id, // Manter referência ao ID original do equipante
      equipanteEventoId: equipanteEventoId, // Preservar ID do EquipanteEvento (raiz)
      // Recriar objetos aninhados com novas referências
      eventos: newEventos,
      presencareuniaos: newPresencareuniaos,
      ...rest, // Outras propriedades do objeto raiz (circulodirigentes, etc.)
      // Observacao/Nota do evento (não as do cadastro da pessoa)
      ...(eventObservacao !== undefined ? { Observacao: eventObservacao } : {}),
      ...(eventNota !== undefined ? { Nota: eventNota } : {}),
    };
    
    return flattened;
  });
  
  return result;
}

export const PAYMENT_PROXY_REFS = [
  "email",
  "profile",
  "home",
  "inscricao_concluida",
  "whatsapp",
  "lancamento_sheet",
  "curso_inscricao",
] as const;

export type PaymentLinkRef = (typeof PAYMENT_PROXY_REFS)[number];

export function buildPaymentProxyUrl(preferenceId: string, ref?: PaymentLinkRef): string {
  const base = `${getApiBaseUrl()}/pagamento/redirect/${preferenceId}`;
  if (ref) {
    return `${base}?ref=${encodeURIComponent(ref)}`;
  }
  return base;
}

/**
 * Link público de pagamento para checkout transparente (ex.: matrícula de curso).
 * O backend entrega o path relativo (`/{login}/{curso}/cursos/pagamento/{id}`);
 * aqui prefixamos a origem do app para gerar a URL absoluta copiável.
 */
export function buildCheckoutTransparenteUrl(pathRelativo: string): string {
  const path = String(pathRelativo ?? "").trim();
  if (!path) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Monta URL absoluta de matrícula (checkout transparente).
 * Prefere o path do backend; se ausente, monta a partir de login/curso/participanteId.
 */
export function buildLinkMatriculaCheckoutTransparente(params: {
  linkPagamentoTransparente?: string | null;
  loginIdentificador?: string | null;
  cursoIdentificador?: string | null;
  participanteId?: number | null;
  TokenInscricao?: string | null;
}): string | null {
  const fromBackend = String(params.linkPagamentoTransparente ?? "").trim();
  if (fromBackend) {
    return buildCheckoutTransparenteUrl(fromBackend);
  }

  const cursoSeg = String(params.cursoIdentificador ?? "").trim();
  const participanteId = Number(params.participanteId);
  if (!cursoSeg || !Number.isFinite(participanteId) || participanteId <= 0) {
    return null;
  }

  const loginSeg = String(params.loginIdentificador ?? "").trim();
  const path = loginSeg
    ? `/${encodeURIComponent(loginSeg)}/${encodeURIComponent(cursoSeg)}/cursos/pagamento/${participanteId}`
    : `/${encodeURIComponent(cursoSeg)}/cursos/pagamento/${participanteId}`;
  return buildCheckoutTransparenteUrl(comTokenInscricao(path, participanteId, params.TokenInscricao ?? undefined));
}

/**
 * Link público de pagamento de voluntário (checkout transparente).
 * Espelha `buildPagamentoVoluntarioPublicPath` do backend.
 */
export function buildLinkVoluntarioCheckoutTransparente(params: {
  loginIdentificador?: string | null;
  eventoIdentificador?: string | null;
  equipanteEventoId?: number | null;
  TokenEquipanteEvento?: string | null;
}): string | null {
  const loginSeg = String(params.loginIdentificador ?? "").trim();
  const cfgSeg = String(params.eventoIdentificador ?? "").trim();
  const equipanteEventoId = Number(params.equipanteEventoId);
  if (
    !loginSeg ||
    !cfgSeg ||
    !Number.isFinite(equipanteEventoId) ||
    equipanteEventoId <= 0
  ) {
    return null;
  }
  const path = `/${encodeURIComponent(loginSeg)}/${encodeURIComponent(cfgSeg)}/voluntario/pagamento/${equipanteEventoId}`;
  return buildCheckoutTransparenteUrl(params.TokenEquipanteEvento ? `${path}?t=${encodeURIComponent(params.TokenEquipanteEvento)}` : path);
}

/**
 * Link público de pagamento de participante (checkout transparente).
 * Espelha `buildPagamentoParticipantePublicPath` do backend (eventos não-ensino).
 */
export function buildLinkParticipanteCheckoutTransparente(params: {
  loginIdentificador?: string | null;
  eventoIdentificador?: string | null;
  participanteId?: number | null;
  TokenInscricao?: string | null;
}): string | null {
  const loginSeg = String(params.loginIdentificador ?? "").trim();
  const cfgSeg = String(params.eventoIdentificador ?? "").trim();
  const participanteId = Number(params.participanteId);
  if (
    !loginSeg ||
    !cfgSeg ||
    !Number.isFinite(participanteId) ||
    participanteId <= 0
  ) {
    return null;
  }
  const path = `/${encodeURIComponent(loginSeg)}/${encodeURIComponent(cfgSeg)}/participante/pagamento/${participanteId}`;
  return buildCheckoutTransparenteUrl(comTokenInscricao(path, participanteId, params.TokenInscricao ?? undefined));
}

/**
 * Preprocess para campos de data em schemas Zod.
 * Trata corretamente datas no formato brasileiro DD/MM/YYYY.
 * Use com z.preprocess(zodDatePreprocess, z.date().nullish())
 */
export function zodDatePreprocess(val: unknown): Date | null {
  if (val === null || val === undefined || val === "") return null;
  if (val instanceof Date) return val;
  if (typeof val === "string") {
    // Se for string no formato DD/MM/YYYY, parsear corretamente
    const brDateMatch = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (brDateMatch) {
      const [, day, month, year] = brDateMatch;
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    }
    // Se for formato ISO (YYYY-MM-DD ou com T), deixar o Date parsear
    return new Date(val);
  }
  return null;
}
